import { useRef, useState } from 'react';
import { de, MAX_IMAGES_PER_DAMAGE, VIDEO_MAX_PER_DAMAGE } from '@fsv/core';
import type { NewImage, UploadProgress } from '@fsv/data';
import { tx } from '@fsv/data';
import { preparePhoto } from '../lib/photo';
import { prepareVideo, VideoRefused } from '../lib/video';
import { Icon } from './Icon';
import './PhotoPicker.css';

/**
 * Two ways in: the camera (capture=environment opens the rear camera on
 * phones) and the gallery. Every photo is shrunk and stripped on the device
 * before anything else happens; the overview flag is a star per photo.
 *
 * `errors[i]` marks a photo the server refused (it stays for another try);
 * `progress` shows the running upload, one step per photo.
 */
export function PhotoPicker({
  photos,
  onChange,
  existingCount = 0,
  existingVideoCount = 0,
  errors,
  progress,
}: {
  photos: NewImage[];
  onChange: (photos: NewImage[]) => void;
  existingCount?: number;
  /** Videos the damage already has (0018); they have a limit of their own. */
  existingVideoCount?: number;
  errors?: readonly (string | null)[] | undefined;
  progress?: UploadProgress | null | undefined;
}) {
  const camera = useRef<HTMLInputElement>(null);
  const gallery = useRef<HTMLInputElement>(null);
  const recorder = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(0);
  // The video being prepared on the device, 0…1 (stage 2: it takes seconds, and the person should see it move).
  const [preparing, setPreparing] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const chosenVideos = photos.filter((p) => p.kind === 'video').length;
  const room = MAX_IMAGES_PER_DAMAGE - existingCount - (photos.length - chosenVideos);
  const videoRoom = VIDEO_MAX_PER_DAMAGE - existingVideoCount - chosenVideos;

  const add = async (files: FileList | null) => {
    if (!files?.length) return;
    setError(null);
    const all = [...files];
    const videos = all.filter((f) => f.type.startsWith('video/'));
    const images = all.filter((f) => !f.type.startsWith('video/'));
    const imageList = images.slice(0, Math.max(0, room));
    const videoList = videos.slice(0, Math.max(0, videoRoom));
    if (images.length > imageList.length) setError(tx.create.tooManyPhotos(MAX_IMAGES_PER_DAMAGE));
    if (videos.length > videoList.length) setError(tx.video.tooMany(VIDEO_MAX_PER_DAMAGE));
    setBusy(imageList.length + videoList.length);
    const prepared: NewImage[] = [];
    for (const file of imageList) {
      try {
        // First photo of a damage defaults to "overview" when it is the only one so far.
        prepared.push(await preparePhoto(file, photos.length + prepared.length === 0 && existingCount === 0));
      } catch {
        setError(tx.create.photoFailed);
      }
      setBusy((b) => b - 1);
    }
    for (const file of videoList) {
      setPreparing(0);
      try {
        prepared.push(await prepareVideo(file, setPreparing));
      } catch (e) {
        setError(e instanceof VideoRefused ? e.message : tx.video.cannotConvert);
      }
      setPreparing(null);
      setBusy((b) => b - 1);
    }
    onChange([...photos, ...prepared]);
  };

  return (
    <div className="photos">
      <div className="field__label">{de.damage.photos}</div>
      <p className="field__hint">{tx.create.photoHint}</p>
      {(photos.length > 0 || busy > 0) && (
        <ul className="photos__grid">
          {photos.map((p, i) => (
            <li key={p.thumbDataUrl.slice(-32) + i} className={`photos__item${errors?.[i] ? ' photos__item--failed' : ''}`}>
              <img src={p.thumbDataUrl} alt={p.kind === 'video' ? tx.video.label(i + 1) : `${de.damage.photos} ${i + 1}`} />
              {p.kind === 'video' && (
                <span className="photos__video" aria-hidden="true">
                  <Icon name="play" size={14} />
                  {p.durationMs ? tx.video.duration(p.durationMs / 1000) : ''}
                </span>
              )}
              {errors?.[i] && <p className="photos__error">{errors[i]}</p>}
              {p.kind !== 'video' && (
              <button
                type="button"
                className={`photos__flag${p.isOverview ? ' is-on' : ''}`}
                aria-pressed={p.isOverview}
                onClick={() => onChange(photos.map((q, j) => (j === i ? { ...q, isOverview: !q.isOverview } : q)))}
              >
                <Icon name="star" size={16} />
                {tx.create.overviewToggle}
              </button>
              )}
              <button type="button" className="photos__remove" aria-label={tx.create.removePhoto} onClick={() => onChange(photos.filter((_, j) => j !== i))}>
                <Icon name="close" size={18} />
              </button>
            </li>
          ))}
          {Array.from({ length: busy }, (_, i) => (
            <li key={`busy-${i}`} className="photos__item photos__item--busy" aria-live="polite">
              {i === 0 && preparing !== null ? (
                <span className="photos__prep" data-testid="video-preparing">
                  <progress max={1} value={preparing} />
                  {tx.video.preparing(Math.round(preparing * 100))}
                </span>
              ) : (
                tx.create.processing
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="row">
        <button type="button" className="btn btn--ink" onClick={() => camera.current?.click()} disabled={room <= 0}>
          <Icon name="camera" />
          {tx.create.addPhotoCamera}
        </button>
        <button type="button" className="btn" onClick={() => recorder.current?.click()} disabled={videoRoom <= 0 || preparing !== null}>
          <Icon name="video" />
          {tx.video.add}
        </button>
        <button type="button" className="btn" onClick={() => gallery.current?.click()} disabled={room <= 0 && videoRoom <= 0}>
          <Icon name="image" />
          {tx.create.addPhotoGallery}
        </button>
      </div>
      <input ref={camera} type="file" accept="image/*" capture="environment" hidden onChange={(e) => void add(e.currentTarget.files).then(() => (e.target.value = ''))} />
      <input ref={gallery} type="file" accept="image/*,video/*" multiple hidden onChange={(e) => void add(e.currentTarget.files).then(() => (e.target.value = ''))} />
      <input ref={recorder} type="file" accept="video/*" capture="environment" hidden onChange={(e) => void add(e.currentTarget.files).then(() => (e.target.value = ''))} />
      {error && <p className="field__error">{error}</p>}
      {errors?.some(Boolean) && <p className="field__error" role="alert">{tx.upload.retryHint}</p>}
      {progress && progress.done < progress.total && (
        <p className="photos__progress" role="status" aria-live="polite">
          <progress max={progress.total} value={progress.done + (progress.fraction ?? 0)} />
          {progress.fraction !== undefined && progress.fraction < 1
            ? tx.video.uploading(Math.round(progress.fraction * 100))
            : tx.upload.progress(progress.done, progress.total)}
        </p>
      )}
    </div>
  );
}
